"use client";

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { createAuthClient } from "../client";
import { AuthSdkError } from "../errors";
import { MemoryTokenStorage } from "../storage";

import { AuthContext } from "./context";

import type { AuthClient, AuthUser, OAuthProvider, PkceStorage } from "../types";
import type { AuthContextValue, AuthProviderProps } from "./types";

export function AuthProvider({
  authUrl,
  clientId,
  redirectUri,
  storage,
  pkceStorage: defaultPkceStorage,
  client: externalClient,
  children,
}: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  const client = useMemo<AuthClient>(() => {
    if (externalClient) return externalClient;

    if (!authUrl || !clientId || !redirectUri) {
      throw new AuthSdkError(
        "invalid_config",
        "AuthProvider requires either a client prop or authUrl, clientId, and redirectUri",
      );
    }

    return createAuthClient({
      authUrl,
      clientId,
      redirectUri,
      storage: storage ?? new MemoryTokenStorage(),
    });
  }, [externalClient, authUrl, clientId, redirectUri, storage]);

  const scheduleRefresh = useCallback(
    (expiresIn: number) => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      // Refresh 5 minutes before expiry, minimum 10 seconds
      const delayMs = Math.max((expiresIn - 300) * 1000, 10_000);

      refreshTimeoutRef.current = setTimeout(async () => {
        try {
          const tokens = await client.refresh();
          const refreshedUser = await client.getUser();
          setUser(refreshedUser);
          scheduleRefresh(tokens.expiresIn);
        } catch {
          setUser(null);
        }
      }, delayMs);
    },
    [client],
  );

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const tokens = await client.getTokens();
        if (!tokens) {
          // No stored tokens — try a cookie-based refresh (httpOnly cookie may exist)
          try {
            const refreshed = await client.refresh();
            if (!cancelled) {
              const currentUser = await client.getUser();
              setUser(currentUser);
              scheduleRefresh(refreshed.expiresIn);
            }
          } catch {
            // No cookie either — user is unauthenticated
          }
          return;
        }

        try {
          const currentUser = await client.getUser();
          if (!cancelled) {
            setUser(currentUser);
            scheduleRefresh(tokens.expiresIn);
          }
        } catch {
          // Access token likely expired — try refresh
          try {
            const refreshed = await client.refresh();
            if (!cancelled) {
              const currentUser = await client.getUser();
              setUser(currentUser);
              scheduleRefresh(refreshed.expiresIn);
            }
          } catch {
            // Refresh also failed — user is unauthenticated
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void init();

    return () => {
      cancelled = true;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [client, scheduleRefresh]);

  const login = useCallback(
    async (options?: { provider?: OAuthProvider; pkceStorage?: PkceStorage }) => {
      const pkce = options?.pkceStorage ?? defaultPkceStorage;
      if (!pkce) {
        throw new AuthSdkError(
          "pkce_missing",
          "pkceStorage is required for login — pass it to login() or as a prop to AuthProvider",
        );
      }

      const url = await client.login({
        provider: options?.provider,
        pkceStorage: pkce,
      });

      window.location.href = url;
    },
    [client, defaultPkceStorage],
  );

  const handleCallback = useCallback(
    async (options: { code: string; state?: string; pkceStorage?: PkceStorage }) => {
      const pkce = options.pkceStorage ?? defaultPkceStorage;
      if (!pkce) {
        throw new AuthSdkError(
          "pkce_missing",
          "pkceStorage is required for handleCallback — pass it or set it on AuthProvider",
        );
      }

      const tokens = await client.handleCallback({
        code: options.code,
        state: options.state,
        pkceStorage: pkce,
      });

      const currentUser = await client.getUser();
      setUser(currentUser);
      scheduleRefresh(tokens.expiresIn);
    },
    [client, defaultPkceStorage, scheduleRefresh],
  );

  const loginWithIdentityToken = useCallback(
    async (provider: OAuthProvider, identityToken: string) => {
      const tokens = await client.loginWithIdentityToken(provider, identityToken);
      const currentUser = await client.getUser();
      setUser(currentUser);
      scheduleRefresh(tokens.expiresIn);
    },
    [client, scheduleRefresh],
  );

  const logout = useCallback(async () => {
    await client.logout();
    setUser(null);
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
  }, [client]);

  const deleteAccount = useCallback(async () => {
    await client.deleteAccount();
    setUser(null);
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
  }, [client]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      login,
      handleCallback,
      loginWithIdentityToken,
      logout,
      deleteAccount,
    }),
    [user, isLoading, login, handleCallback, loginWithIdentityToken, logout, deleteAccount],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new AuthSdkError("invalid_config", "useAuth must be used within an AuthProvider");
  }
  return context;
}

export function useUser(): AuthUser {
  const { user } = useAuth();
  if (!user) {
    throw new AuthSdkError(
      "invalid_config",
      "useUser requires an authenticated user — check isAuthenticated before calling",
    );
  }
  return user;
}
